'use client'

import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { formatGems } from '../helpers/helpers'
import Image from 'next/image'

// `offer` (experiment arm B only) folds the promo that would otherwise be a
// banner into this CTA: a small badge, and the offer's own destination. Absent
// in the control arm, where the banner still carries it.
const GemButton = ({ style, cost, offer }) => {
  const { t } = useTranslation()

  const { gems, displayPlan, userDataLoaded } = useAuth()

  // `gems` initializes to 0 (never null), so keying off it would flash a fake "0".
  // Wait for the real balance to arrive (`userDataLoaded`) before showing the number.
  const loading = !userDataLoaded

  const gemText = loading ? '' : formatGems(gems)

  // Tier the font size so large counts shrink to fit the fixed-width slot,
  // instead of widening the pill (Tailwind has no true fit-to-width).
  const gemSizeClass =
    gemText.length >= 7
      ? 'text-[10px]'
      : gemText.length >= 6
        ? 'text-xs'
        : 'text-sm'

  const renderGemButtonContent = () => {
    if (loading) {
      // Soft placeholder bar in the same slot (no reflow). The shimmer animation
      // lives on the whole pill below, so this stays static.
      return (
        <span
          className="h-3.5 w-10 rounded-full bg-white/15"
          aria-hidden="true"
        />
      )
    }

    if (cost !== undefined) {
      return (
        <span>
          <span className="font-bold mr-0.5">{cost}</span>/
          <span className="ml-0.5">{formatGems(gems)}</span>
        </span>
      )
    }
    return gemText
  }

  // DISPLAY ONLY — `displayPlan` optimistically reflects the cached plan while subscription
  // loads, so a returning paid user sees the gem pill instead of the "Upgrade" rocket. The
  // gem NUMBER below stays on the real `gems` value (skeleton until it loads); no gems or
  // access are ever derived from the cookie.
  const isPaid = Boolean(displayPlan && displayPlan !== 'Free')

  // Pro theming, borrowed from the earn-gems gold CTA so the tier reads the same
  // everywhere: amber-tinted glass, a soft gold glow, and the gradient-clipped
  // label from that card's heading. Deliberately no sheen sweep — earn-gems makes
  // the point itself ("a permanently glowing cell stops registering as a signal"),
  // and this one sits on every page.
  const proSurface =
    'border border-amber-400/40 bg-amber-500/[0.12] shadow-[0_0_18px_-6px_rgba(251,191,36,0.55)] transition-colors hover:bg-amber-500/20'
  const proLabel =
    'bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-400 bg-clip-text text-transparent'

  // Experiment arm B. Sits on the pill's top-right corner, outside the flow so
  // it can't change the row's width — the header has no slack to give.
  const offerBadge = offer ? (
    <span className="pointer-events-none absolute -right-1.5 -top-1.5 rounded-full border border-[var(--background-bg-color)] bg-[#F2AF50] px-1.5 py-px text-[10px] font-extrabold leading-tight text-[#1a1206] shadow-sm">
      {offer.label}
    </span>
  ) : null

  return (
    <div className="flex justify-center align-center items-center space-x-1">
      {isPaid ? (
        <Link
          className={`relative flex text-white justify-center align-center items-center space-x-1 p-3 rounded-2xl border border-gray-500 group ${
            loading
              ? 'skeleton-shimmer'
              : 'hover:bg-[var(--foreground-bg-color)]'
          }`}
          style={style}
          href={
            // The offer's destination wins: a gems discount has to land on
            // /gems even for Plus/Premium, whose pill normally goes to /upgrade.
            offer?.href ||
            (displayPlan === 'Studio' || displayPlan === 'Pro'
              ? '/gems'
              : '/upgrade')
          }
        >
          {offerBadge}
          <Image
            priority
            src="/assets/icons/gem.svg"
            alt="Gem"
            height={20}
            width={20}
            className={`gemIcon ${loading ? 'opacity-50' : ''}`}
          />
          <span
            className={
              cost !== undefined
                ? 'font-semibold text-sm'
                : `font-semibold flex items-center justify-start w-12 tabular-nums leading-none ${gemSizeClass}`
            }
          >
            {renderGemButtonContent()}
          </span>
        </Link>
      ) : (
        /* Gold-themed but quiet: the loud version of this CTA is the discount
           banner directly below, and both shouting reads as desperate. The
           label says what tapping does rather than naming a tier — "Pro" was a
           space compromise from when this shared a row with the nav buttons.
           Same 2.6rem height as the gem pill it stands in for, so the header
           row is laid out identically on both tiers. */
        <Link
          href={offer?.href || '/upgrade'}
          aria-label={t('upgrade')}
          style={style}
          className={`relative flex items-center justify-center gap-1.5 rounded-2xl px-3.5 font-semibold ${proSurface}`}
        >
          {offerBadge}
          <span className="text-sm leading-none">🔥</span>
          {/* Falls back to the tier name under 360px, where the longest locales
              ("Mise à niveau", "アップグレード") would push this absolutely
              positioned cluster over the wordmark. 360px and up — every current
              phone bar the 320px holdouts — gets the verb. */}
          <span className={`whitespace-nowrap text-xs ${proLabel}`}>
            <span className="hidden min-[360px]:inline">{t('upgrade')}</span>
            <span className="min-[360px]:hidden">Pro</span>
          </span>
        </Link>
      )}
    </div>
  )
}

export default GemButton
